import React, { useRef, useState, useEffect, Suspense,useMemo } from 'react';
import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import robotoFont from '../../../fonts/Simply_Rounded_Bold.json';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';



const font = new FontLoader().parse(robotoFont);

const defaultDIM = {
  width: 65,
  height: 33,
  depth: 4.5,
  cornerRadius: 5,
  borderWidth: 2,
  borderHeight: 2,
  textPlacaSize: 10,
  textPlacaHeight: 2.4,
  textExtraSize: 7.5,
  textExtraHeight: 2.2,
  textPlacaYOffset: 2.5,
  textExtraYOffset: -8,
};

function crearTextoMesh(texto, size, height, yOffset, depth) {
  const geometry = new TextGeometry(texto, {
    font,
    size,
    height,
    curveSegments: 8,
    bevelEnabled: false,
  });
  geometry.computeBoundingBox();
  geometry.center();
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ color: '#000000' })
  );
  mesh.position.y = yOffset;
  mesh.position.z = depth + 0.1;
  return mesh;
}

function crearBaseConEsquinasRedondeadas(dim, agregarAgujero, agujeroDerecha) {
  const shape = new THREE.Shape();
  const w = dim.width / 2;
  const h = dim.height / 2;
  const r = dim.cornerRadius;

  // contorno base
  shape.moveTo(-w + r, -h);
  shape.lineTo(w - r, -h);
  shape.quadraticCurveTo(w, -h, w, -h + r);
  shape.lineTo(w, h - r);
  shape.quadraticCurveTo(w, h, w - r, h);
  shape.lineTo(-w + r, h);
  shape.quadraticCurveTo(-w, h, -w, h - r);
  shape.lineTo(-w, -h + r);
  shape.quadraticCurveTo(-w, -h, -w + r, -h);

  // agujero opcional
  if (agregarAgujero) {
    const hole = new THREE.Path();
    const x = agujeroDerecha ? w - 6 : -w + 6;
    const y = h - 6;
    hole.absarc(x, y, 2.2, 0, Math.PI * 2, false);
    shape.holes.push(hole);
  }

  const geometry = new THREE.ExtrudeGeometry(shape, { depth: dim.depth, bevelEnabled: false });
  geometry.center();
  return geometry;
}

function crearMarcoElevado(dim) {
  const shape = new THREE.Shape();
  const w = dim.width / 2;
  const h = dim.height / 2;
  const r = dim.cornerRadius;

  shape.moveTo(-w + r, -h);
  shape.lineTo(w - r, -h);
  shape.quadraticCurveTo(w, -h, w, -h + r);
  shape.lineTo(w, h - r);
  shape.quadraticCurveTo(w, h, w - r, h);
  shape.lineTo(-w + r, h);
  shape.quadraticCurveTo(-w, h, -w, h - r);
  shape.lineTo(-w, -h + r);
  shape.quadraticCurveTo(-w, -h, -w + r, -h);

  const inner = new THREE.Path();
  const iw = w - dim.borderWidth;
  const ih = h - dim.borderWidth;
  const ir = Math.max(r - 1.5, 0);

  inner.moveTo(-iw + ir, -ih);
  inner.lineTo(iw - ir, -ih);
  inner.quadraticCurveTo(iw, -ih, iw, -ih + ir);
  inner.lineTo(iw, ih - ir);
  inner.quadraticCurveTo(iw, ih, iw - ir, ih);
  inner.lineTo(-iw + ir, ih);
  inner.quadraticCurveTo(-iw, ih, -iw, ih - ir);
  inner.lineTo(-iw, -ih + ir);
  inner.quadraticCurveTo(-iw, -ih, -iw + ir, -ih);

  shape.holes.push(inner);

  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: dim.borderHeight,
    bevelEnabled: false,
  });
  geometry.translate(0, 0, dim.depth);
  return geometry;
}

function Llavero({ placa, textoExtra, color, dim, agregarAgujero, agujeroDerecha }) {
  const groupRef = useRef();

  useEffect(() => {
    const group = new THREE.Group();
    const base = new THREE.Mesh(
      crearBaseConEsquinasRedondeadas(dim, agregarAgujero, agujeroDerecha),
      new THREE.MeshStandardMaterial({ color })
    );
    group.add(base);

    const marco = new THREE.Mesh(
      crearMarcoElevado(dim),
      new THREE.MeshStandardMaterial({ color: '#111111' })
    );
    marco.position.z = -3;
    group.add(marco);

    if (placa) group.add(crearTextoMesh(placa, dim.textPlacaSize, dim.textPlacaHeight, dim.textPlacaYOffset, dim.depth));
    if (textoExtra) group.add(crearTextoMesh(textoExtra, dim.textExtraSize, dim.textExtraHeight, dim.textExtraYOffset, dim.depth));

    groupRef.current.clear();
    groupRef.current.add(...group.children);
  }, [placa, textoExtra, color, dim, agregarAgujero, agujeroDerecha]);

  return <group ref={groupRef} />;
}

function ExportarSTL({ placa, textoExtra, color, dim, agregarAgujero, agujeroDerecha }) {
  const handleExport = () => {
    const geometries = [];

    const base = new THREE.Mesh(
      crearBaseConEsquinasRedondeadas(dim, agregarAgujero, agujeroDerecha),
      new THREE.MeshStandardMaterial()
    );
    base.updateMatrix();
    base.geometry.applyMatrix4(base.matrix);
    geometries.push(base.geometry);

    const marco = new THREE.Mesh(crearMarcoElevado(dim), new THREE.MeshStandardMaterial());
    marco.position.z = -3;
    marco.updateMatrix();
    marco.geometry.applyMatrix4(marco.matrix);
    geometries.push(marco.geometry);

    if (placa) {
      const textoPlaca = new TextGeometry(placa, { font, size: dim.textPlacaSize, height: dim.textPlacaHeight, curveSegments: 8 });
      textoPlaca.computeBoundingBox();
      textoPlaca.center();
      textoPlaca.translate(0, dim.textPlacaYOffset, dim.depth / 2);
      geometries.push(textoPlaca);
    }

    if (textoExtra) {
      const textoExtraG = new TextGeometry(textoExtra, { font, size: dim.textExtraSize, height: dim.textExtraHeight, curveSegments: 8 });
      textoExtraG.computeBoundingBox();
      textoExtraG.center();
      textoExtraG.translate(0, dim.textExtraYOffset, dim.depth / 2);
      geometries.push(textoExtraG);
    }

    const combined = mergeGeometries(geometries, true);
    const mesh = new THREE.Mesh(combined);
    const exporter = new STLExporter();
    const stlString = exporter.parse(mesh);
    const blob = new Blob([stlString], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `llavero_${placa}.stl`;
    a.click();
  };

  return <button onClick={handleExport}>📥 Solicitar diseño (Descargar STL)</button>;
}

function ParamControl({ label, value, onChange, step = 0.1, min = 0 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 4, background: '#f1f1f1', borderRadius: 8, padding: 6 }}>
      <span>{label}</span>
      <button onClick={() => onChange(Math.max(value - step, min))}>➖</button>
      <span style={{ width: 40, textAlign: 'center' }}>{value.toFixed(1)}</span>
      <button onClick={() => onChange(value + step)}>➕</button>
    </div>
  );
}

function LuzDireccionalControlable({ luzRef, targetRef }) {
  useEffect(() => {
    if (luzRef.current && targetRef.current) {
      luzRef.current.target = targetRef.current;
    }
  }, [luzRef, targetRef]);

  return (
    <>
      <directionalLight
        ref={luzRef}
        position={[5, 5, 5]}
        intensity={1.2}
        castShadow
      />
      <object3D ref={targetRef} position={[0, 0, 0]} />
    </>
  );
}

function Vista3DLlavero({ placa, textoExtra, color, dim, autoRotate }) {
  const groupRef = useRef();

  useEffect(() => {
    const group = new THREE.Group();

    // Base con color personalizado
    const base = new THREE.Mesh(
      crearBaseConEsquinasRedondeadas(dim),
      new THREE.MeshStandardMaterial({ color })
    );
    group.add(base);

    // Marco negro
    const marco = new THREE.Mesh(
      crearMarcoElevado(dim),
      new THREE.MeshStandardMaterial({ color: '#000000' })
    );
    marco.position.z = -2.8;
    group.add(marco);

    // Texto placa negro
    if (placa) {
      const textoPlaca = new TextGeometry(placa, {
        font,
        size: dim.textPlacaSize,
        height: dim.textPlacaHeight,
        curveSegments: 8,
      });
      textoPlaca.computeBoundingBox();
      textoPlaca.center();
      textoPlaca.translate(0, dim.textPlacaYOffset, dim.depth / 2);

      const textoPlacaMesh = new THREE.Mesh(
        textoPlaca,
        new THREE.MeshStandardMaterial({ color: '#000000' })
      );
      group.add(textoPlacaMesh);
    }

    // Texto extra negro
    if (textoExtra) {
      const textoExtraG = new TextGeometry(textoExtra, {
        font,
        size: dim.textExtraSize,
        height: dim.textExtraHeight,
        curveSegments: 8,
      });
      textoExtraG.computeBoundingBox();
      textoExtraG.center();
      textoExtraG.translate(0, dim.textExtraYOffset, dim.depth / 2);

      const textoExtraMesh = new THREE.Mesh(
        textoExtraG,
        new THREE.MeshStandardMaterial({ color: '#000000' })
      );
      group.add(textoExtraMesh);
    }

    // Limpiar grupo y añadir nuevos hijos
    groupRef.current.clear();
    groupRef.current.add(...group.children);
  }, [placa, textoExtra, color, dim]);

  useFrame(() => {
    if (autoRotate && groupRef.current) {
      groupRef.current.rotation.y += 0.01;
    }
  });

  return <group ref={groupRef} />;
}





export default function GeneradorLlavero() {
  const [placa, setPlaca] = useState('XYZ-123');
  const [textoExtra, setTextoExtra] = useState('COLOMBIA');
  const [color, setColor] = useState('#ffcc00');
  const [dim, setDim] = useState(defaultDIM);
  const [showConfig, setShowConfig] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [agregarAgujero, setAgregarAgujero] = useState(true);
  const [agujeroDerecha, setAgujeroDerecha] = useState(false);

  const updateDim = (key, val) => setDim((prev) => ({ ...prev, [key]: val }));

  const [autoRotate, setAutoRotate] = useState(true);
  const orbitRef = useRef();
  const luzRef = useRef();
  const luzTargetRef = useRef();

  return (
    <div style={{ padding: 20, background: 'white' }}>
      <h2>🧩 Editor de Llavero STL</h2>

      <div style={{ marginBottom: 20 }}>
        <button
          onClick={() => setShowConfig(!showConfig)}
          style={{ background: '#ddd', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: '16px' }}>
          ⚙️ Configuración
        </button>

        {showConfig && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <button onClick={() => setActiveSection(activeSection === 'llavero' ? null : 'llavero')} style={{ flex: 1, background: '#faac2a', border: 'none', borderRadius: 8, padding: 10, fontWeight: 'bold', cursor: 'pointer' }}>📐 Llavero</button>
              <button onClick={() => setActiveSection(activeSection === 'texto' ? null : 'texto')} style={{ flex: 1, background: '#2aeafa', border: 'none', borderRadius: 8, padding: 10, fontWeight: 'bold', cursor: 'pointer' }}>✍️ Texto</button>
            </div>

            {activeSection === 'llavero' && (
              <div style={{ background: '#faac2a', borderRadius: 12, padding: 12 }}>
                <ParamControl label="Ancho" value={dim.width} onChange={(v) => updateDim('width', v)} />
                <ParamControl label="Alto" value={dim.height} onChange={(v) => updateDim('height', v)} />
                <ParamControl label="Profundidad" value={dim.depth} onChange={(v) => updateDim('depth', v)} />
                <ParamControl label="Radio esquinas" value={dim.cornerRadius} onChange={(v) => updateDim('cornerRadius', v)} />
                <ParamControl label="Borde ancho" value={dim.borderWidth} onChange={(v) => updateDim('borderWidth', v)} />
                <ParamControl label="Borde alto" value={dim.borderHeight} onChange={(v) => updateDim('borderHeight', v)} />

                {/* 🔲 NUEVOS CHECKBOXES */}
                <div style={{ marginTop: 10 }}>
                  <label>
                    <input type="checkbox" checked={agregarAgujero} onChange={(e) => setAgregarAgujero(e.target.checked)} />
                    {' '}Agregar agujero
                  </label>
                </div>
                {agregarAgujero && (
                  <div style={{ marginTop: 6 }}>
                    <label>
                      <input type="checkbox" checked={agujeroDerecha} onChange={(e) => setAgujeroDerecha(e.target.checked)} />
                      {' '}Mover agujero a esquina derecha
                    </label>
                  </div>
                )}
              </div>
            )}

            {activeSection === 'texto' && (
              <div style={{ background: '#2aeafa', borderRadius: 12, padding: 12 }}>
                <ParamControl label="Texto Placa" value={dim.textPlacaSize} onChange={(v) => updateDim('textPlacaSize', v)} />
                <ParamControl label="Alto Placa" value={dim.textPlacaHeight} onChange={(v) => updateDim('textPlacaHeight', v)} />
                <ParamControl label="Texto Extra" value={dim.textExtraSize} onChange={(v) => updateDim('textExtraSize', v)} />
                <ParamControl label="Alto Extra" value={dim.textExtraHeight} onChange={(v) => updateDim('textExtraHeight', v)} />
                <ParamControl label="Y Placa" value={dim.textPlacaYOffset} onChange={(v) => updateDim('textPlacaYOffset', v)} step={0.5} min={-20} />
                <ParamControl label="Y Extra" value={dim.textExtraYOffset} onChange={(v) => updateDim('textExtraYOffset', v)} step={0.5} min={-20} />
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>
          Placa:
          <input type="text" value={placa} onChange={(e) => setPlaca(e.target.value)} style={{ marginLeft: 10 }} />
        </label>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label>
          Texto inferior:
          <input type="text" value={textoExtra} onChange={(e) => setTextoExtra(e.target.value)} style={{ marginLeft: 10 }} />
        </label>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label>
          Color del llavero:
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ marginLeft: 10 }} />
        </label>
      </div>

      <div style={{ marginBottom: 10 }}>
        <ExportarSTL placa={placa} textoExtra={textoExtra} color={color} dim={dim} agregarAgujero={agregarAgujero} agujeroDerecha={agujeroDerecha} />
      </div>

      {/* Vista 3D */}
      <Canvas shadows camera={{ position: [0, 0, 100], fov: 60 }} style={{ height: 400 }}>
        <ambientLight intensity={0.4} />
        <hemisphereLight skyColor="#ffffff" groundColor="#888888" intensity={0.6} />
        <LuzDireccionalControlable luzRef={luzRef} targetRef={luzTargetRef} />
        <Suspense fallback={null}>
          <Vista3DLlavero placa={placa} textoExtra={textoExtra} color={color} dim={dim} autoRotate={autoRotate} />
        </Suspense>
        <OrbitControls ref={orbitRef} enableZoom enablePan enableRotate />
      </Canvas>

      <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button onClick={() => setAutoRotate(!autoRotate)}>
          {autoRotate ? '⏸️ Detener Rotación' : '🔁 Rotar Automático'}
        </button>
      </div>
    </div>
  );
}
